import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ACCOUNT_TYPE_LABEL,
  ACCOUNT_TYPE_ORDER,
  BALANCE_LABEL,
  chartOfAccountsApi,
  type Account,
  type AccountTreeNode,
  type AccountType,
  type AccountsSummary,
} from '../lib/api/chart-of-accounts';
import { AccountFormModal, type AccountFormMode } from './AccountFormModal';
import { SettingsImportModal } from './SettingsImportModal';

interface EditState {
  mode: AccountFormMode;
  editing?: Account;
  parent?: Account;
}

export function ChartOfAccountsSection() {
  const { user } = useAuth();
  const isKTT = user?.role === 'KTT';

  const [tree, setTree] = useState<AccountTreeNode[]>([]);
  const [summary, setSummary] = useState<AccountsSummary>({ total: 0, inactive: 0, leafCount: 0, byType: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<AccountType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editState, setEditState] = useState<EditState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 220);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await chartOfAccountsApi.listTree({
        activeOnly: showInactive ? undefined : 'true',
        search: debouncedSearch || undefined,
      });
      setTree(res.items);
      setSummary(res.summary);
      // Auto-expand roots on first load / after search
      if (debouncedSearch) {
        const all = new Set<string>();
        walk(res.items, (n) => all.add(n.code));
        setExpanded(all);
      } else {
        setExpanded((prev) => {
          if (prev.size > 0) return prev;
          const next = new Set<string>();
          for (const root of res.items) next.add(root.code);
          return next;
        });
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [showInactive, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byType = new Map<AccountType, AccountTreeNode[]>();
    for (const root of tree) {
      if (typeFilter !== 'all' && root.type !== typeFilter) continue;
      const arr = byType.get(root.type) ?? [];
      arr.push(root);
      byType.set(root.type, arr);
    }
    return ACCOUNT_TYPE_ORDER
      .filter((t) => byType.has(t))
      .map((t) => ({ type: t, roots: byType.get(t)! }));
  }, [tree, typeFilter]);

  const typeCounts = useMemo(() => {
    const counts: Partial<Record<AccountType, number>> = {};
    walk(tree, (n) => {
      counts[n.type] = (counts[n.type] ?? 0) + 1;
    });
    return counts;
  }, [tree]);

  const toggle = (code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleDelete = async (acc: Account) => {
    if (!window.confirm(`Xoá TK ${acc.code} - ${acc.name}?`)) return;
    try {
      await chartOfAccountsApi.remove(acc.code);
      setToast(`Đã xoá TK ${acc.code}`);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  const handleSaved = () => {
    setEditState(null);
    setToast('Đã lưu tài khoản');
    load();
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <>
      {/* Banner */}
      <div className="coa-banner">
        <div className="coa-banner-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" /></svg>
        </div>
        <div className="coa-banner-meta">
          <div className="coa-banner-title-row">
            <h2>Hệ thống tài khoản</h2>
            <span className="coa-banner-badge">TT 200/2014/TT-BTC</span>
          </div>
          <p>
            Danh mục tài khoản kế toán theo Thông tư 200. Tổng <b>{summary.total}</b> TK
            · <b>{summary.leafCount}</b> TK ghi sổ · <b>{summary.inactive}</b> TK đang tắt.
            {!isKTT && ' Chỉ KTT được sửa.'}
          </p>
        </div>
        <div className="coa-banner-actions">
          <button
            className="pay-btn-outline"
            type="button"
            onClick={() => setShowImport(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            Import COA
          </button>
          {isKTT && (
            <button
              className="pay-btn-dark"
              type="button"
              onClick={() => setEditState({ mode: 'create' })}
            >
              + Thêm TK
            </button>
          )}
        </div>
      </div>

      {/* Bộ lọc + danh sách dùng chung một surface, bảng cuộn độc lập. */}
      <div className="coa-directory">
        <div className="coa-filter-row">
          <div className="coa-pills">
          <button
            type="button"
            className={`coa-pill ${typeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTypeFilter('all')}
          >
            Tất cả <span className="coa-pill-count">{summary.total}</span>
          </button>
          {ACCOUNT_TYPE_ORDER.map((t) => {
            const n = summary.byType[t] ?? 0;
            if (n === 0) return null;
            return (
              <button
                key={t}
                type="button"
                className={`coa-pill ${typeFilter === t ? 'active' : ''}`}
                onClick={() => setTypeFilter(t)}
              >
                {ACCOUNT_TYPE_LABEL[t]} <span className="coa-pill-count">{n}</span>
              </button>
            );
          })}
        </div>
          <div className="coa-filter-right">
          <div className="pay-search coa-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input
              placeholder="Tìm theo mã hoặc tên TK…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="coa-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span className="coa-toggle-switch" aria-hidden="true"><span /></span>
            <span className="coa-toggle-label">Hiện TK tắt</span>
          </label>
          </div>
        </div>

        {/* Tree table */}
        <div className="coa-table-wrap">
        {loading ? (
          <div className="pay-empty">Đang tải…</div>
        ) : err ? (
          <div className="pay-table-error">Lỗi: {err}</div>
        ) : grouped.length === 0 ? (
          <div className="pay-empty">
            <div className="pay-empty-icon">🔍</div>
            <div>Không có TK khớp bộ lọc.</div>
          </div>
        ) : (
          <table className="coa-tbl">
            <thead>
              <tr>
                <th className="col-code">Mã TK</th>
                <th className="col-name">Tên tài khoản</th>
                <th className="col-bal">Số dư mặc định</th>
                <th className="col-status">Trạng thái</th>
                <th className="col-act"></th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <TypeGroup
                  key={g.type}
                  type={g.type}
                  count={typeCounts[g.type] ?? 0}
                  roots={g.roots}
                  expanded={expanded}
                  onToggle={toggle}
                  isKTT={isKTT}
                  onAddChild={(parent) => setEditState({ mode: 'create', parent })}
                  onEdit={(acc) => setEditState({ mode: 'edit', editing: acc })}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {editState && (
        <AccountFormModal
          mode={editState.mode}
          editing={editState.editing}
          parent={editState.parent}
          onClose={() => setEditState(null)}
          onSaved={handleSaved}
        />
      )}
      <SettingsImportModal kind="coa" open={showImport} onClose={() => setShowImport(false)} onDone={load} />

      {toast && <div className="coa-toast">{toast}</div>}
    </>
  );
}

/* ------------------------------------------------------------------ */

function walk(nodes: AccountTreeNode[], fn: (n: AccountTreeNode) => void) {
  for (const n of nodes) {
    fn(n);
    if (n.children.length) walk(n.children, fn);
  }
}

function typeLoaiIndex(type: AccountType): number {
  // Ordinal chapter number theo TT200
  const map: Record<AccountType, number> = {
    asset: 1,
    liability: 3,
    equity: 4,
    revenue: 5,
    expense: 6,
    other: 9,
  };
  return map[type];
}

interface TypeGroupProps {
  type: AccountType;
  count: number;
  roots: AccountTreeNode[];
  expanded: Set<string>;
  onToggle: (code: string) => void;
  isKTT: boolean;
  onAddChild: (parent: Account) => void;
  onEdit: (acc: Account) => void;
  onDelete: (acc: Account) => void;
}

function TypeGroup(props: TypeGroupProps) {
  const { type, count, roots } = props;
  return (
    <>
      <tr className="coa-type-row">
        <td colSpan={5}>
          <span className="coa-type-num">Loại {typeLoaiIndex(type)}</span>
          <span className="coa-type-dot">·</span>
          <span className="coa-type-name">{ACCOUNT_TYPE_LABEL[type]}</span>
          <span className="coa-type-count">{count} TK</span>
        </td>
      </tr>
      {roots.map((root) => (
        <AccountRow
          key={root.code}
          node={root}
          depth={0}
          expanded={props.expanded}
          onToggle={props.onToggle}
          isKTT={props.isKTT}
          onAddChild={props.onAddChild}
          onEdit={props.onEdit}
          onDelete={props.onDelete}
        />
      ))}
    </>
  );
}

interface RowProps {
  node: AccountTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (code: string) => void;
  isKTT: boolean;
  onAddChild: (parent: Account) => void;
  onEdit: (acc: Account) => void;
  onDelete: (acc: Account) => void;
}

function AccountRow(props: RowProps) {
  const { node, depth, expanded } = props;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.code);
  const isParent = !node.isLeaf;
  const padLeft = 16 + depth * 24;

  return (
    <>
      <tr className={`coa-row ${isParent ? 'is-parent' : 'is-leaf'} ${node.active ? '' : 'is-off'}`}>
        <td className="col-code">
          <div className="coa-code-cell" style={{ paddingLeft: padLeft }}>
            {hasChildren ? (
              <button
                type="button"
                className={`coa-chev ${isOpen ? 'open' : ''}`}
                onClick={() => props.onToggle(node.code)}
                aria-label={isOpen ? 'Thu gọn' : 'Mở rộng'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            ) : (
              <span className="coa-chev-spacer" />
            )}
            <span className={`coa-code-pill ${isParent ? 'parent' : ''}`}>{node.code}</span>
          </div>
        </td>
        <td className="col-name">
          <span className={isParent ? 'coa-name-parent' : 'coa-name'}>{node.name}</span>
          {isParent && <span className="coa-tag-parent">TK cha</span>}
        </td>
        <td className="col-bal">
          <span className={`coa-bal-pill ${node.defaultBalance}`}>{BALANCE_LABEL[node.defaultBalance]}</span>
        </td>
        <td className="col-status">
          <span className={`coa-status-pill ${node.active ? 'on' : 'off'}`}>
            {node.active ? 'Bật' : 'Tắt'}
          </span>
        </td>
        <td className="col-act">
          {props.isKTT && (
            <div className="coa-row-actions">
              {isParent && (
                <button
                  type="button"
                  className="coa-icon-btn"
                  title="Thêm TK con"
                  onClick={() => props.onAddChild(node)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                </button>
              )}
              <button
                type="button"
                className="coa-icon-btn"
                title="Sửa"
                onClick={() => props.onEdit(node)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
              </button>
              {!hasChildren && (
                <button
                  type="button"
                  className="coa-icon-btn danger"
                  title="Xoá"
                  onClick={() => props.onDelete(node)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
      {isOpen &&
        node.children.map((child) => (
          <AccountRow
            key={child.code}
            node={child}
            depth={depth + 1}
            expanded={props.expanded}
            onToggle={props.onToggle}
            isKTT={props.isKTT}
            onAddChild={props.onAddChild}
            onEdit={props.onEdit}
            onDelete={props.onDelete}
          />
        ))}
    </>
  );
}
